import {
  Tooltip,
  Button,
  Text,
  Heading,
  Input,
  Textarea,
  Form,
  Label,
  Flex,
} from '@buffer-mono/popcorn'
import type * as React from 'react'
import { Component } from 'react'

import countHashtagsInText from '../../utils/HashtagCounter'

const counterLabelStyle = (hasError: boolean): React.CSSProperties => ({
  position: 'absolute',
  right: '16px',
  bottom: '16px',
  fontSize: '10px',
  lineHeight: '12px',
  textAlign: 'center',
  border: hasError
    ? `1px solid var(--color-border-critical)`
    : `1px solid var(--color-border-neutral)`,
  color: hasError ? 'var(--color-text-critical)' : 'var(--color-text-neutral)',
  backgroundColor: hasError
    ? 'var(--color-bg-critical-subtle)'
    : 'var(--color-bg-neutral)',
  borderRadius: 'var(--border-radius-sm)',
  padding: '2px 4px',
})

interface HashtagGroupCreatorProps {
  name?: string | null
  text?: string | null
  isEditing?: boolean
  onChangeGroupName: (name: string) => void
  onChangeGroupText: (text: string) => void
  onCancelHashtagGroup: () => void
  onSaveHashtagGroup?: () => void
  onUpdateHashtagGroup?: () => void
}

interface HashtagGroupCreatorState {
  textareaFocused: boolean
  inputFocused: boolean
  isSaveButtonDisabled: boolean
}

class HashtagGroupCreator extends Component<
  HashtagGroupCreatorProps,
  HashtagGroupCreatorState
> {
  state: HashtagGroupCreatorState = {
    textareaFocused: false,
    inputFocused: false,
    isSaveButtonDisabled: true,
  }

  constructor(props: HashtagGroupCreatorProps) {
    super(props)

    this.handleTextareaChange = this.handleTextareaChange.bind(this)
    this.handleInputChange = this.handleInputChange.bind(this)
    this.disableSaveButton = this.disableSaveButton.bind(this)
    this.handleSubmit = this.handleSubmit.bind(this)
  }

  componentDidUpdate(prevProps: HashtagGroupCreatorProps): void {
    if (
      prevProps.name !== this.props.name ||
      prevProps.text !== this.props.text
    ) {
      this.disableSaveButton()
    }
  }

  handleTextareaChange(event: React.ChangeEvent<HTMLTextAreaElement>): void {
    const { value } = event.target
    this.props.onChangeGroupText(value)
  }

  handleInputChange(event: React.ChangeEvent<HTMLInputElement>): void {
    this.props.onChangeGroupName(event.target.value)
  }

  disableSaveButton(): void {
    const { name, text } = this.props
    const numberHashtagsLeft = countHashtagsInText(text || '')
    const isSaveButtonDisabled =
      numberHashtagsLeft < 0 || !text?.trim() || !name?.trim()
    this.setState({ isSaveButtonDisabled })
  }

  handleSubmit(): void {
    const {
      isEditing,
      onUpdateHashtagGroup,
      onSaveHashtagGroup,
      onCancelHashtagGroup,
    } = this.props
    if (isEditing && onUpdateHashtagGroup) {
      onUpdateHashtagGroup()
    } else if (onSaveHashtagGroup) {
      onSaveHashtagGroup()
    }
    onCancelHashtagGroup()
  }

  render(): JSX.Element {
    const { name, text, isEditing, onCancelHashtagGroup } = this.props
    const numberHashtagsLeft = countHashtagsInText(text || '')
    return (
      <Flex
        direction="column"
        gap="lg"
        fullWidth
        fullHeight
        style={{ padding: '0 var(--space-lg)' }}
      >
        <Heading as="h4">
          {isEditing ? 'Edit Hashtag Group' : 'Create Hashtag Group'}
        </Heading>

        <Form
          autoComplete="off"
          style={{
            width: '100%',
            height: '100%',
            display: 'flex',
            gap: 'var(--space-md)',
            flexDirection: 'column',
          }}
        >
          <Form.Field name="hashtagGroupName">
            <Label htmlFor="hashtagGroupName">Hashtag Group Name</Label>
            <Input
              placeholder="Your hashtag group name"
              id="hashtagGroupName"
              maxLength={200}
              type="text"
              value={name || ''}
              onChange={this.handleInputChange}
              onFocus={() => this.setState({ inputFocused: true })}
              onBlur={() => this.setState({ inputFocused: false })}
            />
          </Form.Field>
          <Form.Field
            name="hashtagGroupContent"
            style={{ position: 'relative' }}
          >
            <Label htmlFor="hashtagGroupContent">Hashtag Group Content</Label>

            <Textarea
              placeholder="Your hashtags"
              id="hashtagGroupContent"
              maxLength={2000}
              value={text || ''}
              onChange={this.handleTextareaChange}
              onFocus={() => this.setState({ textareaFocused: true })}
              onBlur={() => this.setState({ textareaFocused: false })}
              style={{ minHeight: '120px' }}
            />
            <div style={counterLabelStyle(numberHashtagsLeft < 0)}>
              <Tooltip
                content="Instagram will reject posts containing over 30 hashtags"
                side="top"
              >
                <Text as="span" color="inherit">
                  # Remaining: {numberHashtagsLeft}
                </Text>
              </Tooltip>
            </div>
          </Form.Field>
        </Form>
        <Flex gap="xs" justify="end" style={{ marginTop: 'auto' }} fullWidth>
          <Button variant="tertiary" onClick={onCancelHashtagGroup}>
            Cancel
          </Button>
          <Button
            type="submit"
            disabled={this.state.isSaveButtonDisabled}
            onClick={this.handleSubmit}
          >
            Save Hashtag Group
          </Button>
        </Flex>
      </Flex>
    )
  }
}

export default HashtagGroupCreator
